/**
 * CombinedAgenda Component
 * 
 * Hybrid page combining detailed event display from FullAgenda
 * with an integrated sleek timeline for quick navigation.
 * Features interactive tour, timeline toggle, and event highlighting.
 */

import React, { useState, useEffect } from 'react';
import '../App.css';
import './CombinedAgenda.css';

// Shared Components
import ProtectedPageLayout from '../components/shared/ProtectedPageLayout';
import HeroSection from '../components/shared/HeroSection';
import VenueSection from '../components/shared/VenueSection';
import GradientContainer from '../components/shared/GradientContainer';
import ClosingSection from '../components/shared/ClosingSection';

// Specific Components and Utilities
import EventCard from '../components/EventCard';
import { getProcessedEvents, groupEventsByDate } from '../utils/eventUtils';
import { VENUE_INFO } from '../data/eventsData';

function CombinedAgenda() {
  // Component State Management
  const [events, setEvents] = useState([]);
  const [activeEventId, setActiveEventId] = useState(null);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [showTour, setShowTour] = useState(true);

  /**
   * Tour Management Effect
   * Shows tour tooltips for 4 seconds when component mounts
   */
  useEffect(() => {
    if (showTour) {
      const timer = setTimeout(() => {
        setShowTour(false);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showTour]);

  /**
   * Events Data Loading Effect
   * Processes and sorts events, sets initial active event
   */
  useEffect(() => {
    const sortedEvents = getProcessedEvents();
    setEvents(sortedEvents);
    
    // Set first event as active by default
    if (sortedEvents.length > 0) {
      setActiveEventId(sortedEvents[0].id);
    }
  }, []);

  /**
   * Timeline Event Click Handler
   * Navigates to selected event and auto-closes timeline
   */
  const handleTimelineClick = (eventId) => {
    setActiveEventId(eventId);
    
    // Smooth scroll to event element
    const element = document.getElementById(`event-${eventId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Auto-close timeline after interaction
    setTimeout(() => {
      setTimelineOpen(false);
    }, 500);
  };

  /**
   * Timeline Toggle Handler
   * Opens/closes the timeline sidebar
   */
  const toggleTimeline = () => {
    setTimelineOpen(!timelineOpen);
  };

  /**
   * Venue Navigation Handler
   * Scrolls to venue section and closes timeline
   */
  const handleVenueClick = () => {
    const element = document.querySelector('.location-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setTimeout(() => {
      setTimelineOpen(false);
    }, 500);
  };

  // Group events for main display
  const eventsByDate = groupEventsByDate(events);

  return (
    <ProtectedPageLayout authPath="/cmbndgnd8392plw" useScrollAnimations={true}>
      {/* Hero Section */}
      <HeroSection />

      {/* Timeline Toggle Button - Fixed Position */}
      <button 
        className={`timeline-toggle ${timelineOpen ? 'active' : ''}`}
        onClick={toggleTimeline}
        aria-label="Toggle Timeline View"
      >
        <span className="timeline-icon">📅</span>
        <span className="timeline-text">Quick Timeline</span>
      </button>

      {/* Interactive Tour Tooltips */}
      {showTour && (
        <>
          {/* Dark Overlay for Focus */}
          <div className="tour-overlay"></div>
          
          {/* Scroll Down Tutorial */}
          <div className="tour-tooltip tour-scroll">
            <div className="tour-arrow">⬇️</div>
            <div className="tour-text">Scroll down to see different events</div>
          </div>
          
          {/* Timeline Button Tutorial */}
          <div className="tour-tooltip tour-timeline">
            <div className="tour-text">Click here for quick timeline</div>
            <div className="tour-arrow">👆</div>
          </div>
        </>
      )}

      {/* Timeline Sidebar Navigation */}
      <div className={`sleek-timeline ${timelineOpen ? 'open' : ''}`}>
        {/* Timeline Header */}
        <div className="timeline-header">
          <h3>Event Schedule</h3>
          <button className="timeline-close" onClick={toggleTimeline}>×</button>
        </div>
        
        {/* Timeline Content with Scrolling */}
        <div className="timeline-content-wrapper">
          {/* Venue Information in Timeline */}
          <div 
            className="timeline-item timeline-venue"
            onClick={handleVenueClick}
          >
            <div className="timeline-dot venue-dot"></div>
            <div className="timeline-info">
              <div className="timeline-title">📍 Venue Information</div>
              <div className="timeline-time">{VENUE_INFO.name}</div>
              <div className="timeline-date">Location Details</div>
            </div>
          </div>
          
          {/* Event Timeline Items */}
          {events.map((event, index) => {
            // Check if this starts a new day for grouping
            const isNewDay = index === 0 || events[index - 1].date !== event.date;
            const dayLabel = isNewDay ? event.date.replace(', 2025', '') : '';
            
            return (
              <div 
                key={event.id}
                className={`timeline-item ${activeEventId === event.id ? 'active' : ''}`}
                onClick={() => handleTimelineClick(event.id)}
                data-new-day={isNewDay}
                data-day-label={dayLabel}
              >
                <div className="timeline-dot"></div>
                <div className="timeline-info">
                  <div className="timeline-title">{event.title}</div>
                  <div className="timeline-time">{event.time}</div>
                  <div className="timeline-date">{event.date}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <GradientContainer variant="fullAgenda">
        {/* Venue Information Section */}
        <VenueSection />

        {/* Events Section with Highlighting */}
        <div className="agenda">
          {Object.keys(eventsByDate).map((date, dateIndex) => (
            <div key={date} className="day-section">
              {/* Date Header */}
              <div className="day-header">
                <h2>{date}</h2>
              </div>
              
              {/* Event Cards with Highlight Support */}
              {eventsByDate[date].map((event, index) => (
                <div 
                  key={event.id} 
                  id={`event-${event.id}`}
                  className={`event-wrapper ${activeEventId === event.id ? 'highlighted' : ''}`}
                >
                  <EventCard 
                    event={event} 
                    isReversed={index % 2 === 1}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Closing Section */}
        <ClosingSection variant="simple" />
      </GradientContainer>
    </ProtectedPageLayout>
  );
}

export default CombinedAgenda;